/**
 * Cost Analytics Page
 *
 * Detailed cost analysis and breakdown.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { AnalyticsSubNav } from '../components/layout/AnalyticsLayout';
import { useIsPro } from '../hooks/useSubscription';
import { ProUpgradeCard } from '../components/billing/ProUpgradeCard';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { Button } from '../components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import { LoadingSkeleton } from '../components/common/LoadingSkeleton';
import {
  useCostAnalytics,
  formatCost,
  formatPercentage,
  formatNumber,
} from '../hooks/useAnalytics';
import { TimeRange, AgentCostBreakdown } from '../types/analytics';

// Chart colors
const CHART_COLORS = [
  '#3b82f6',
  '#22c55e',
  '#f59e0b',
  '#ef4444',
  '#8b5cf6',
  '#06b6d4',
  '#ec4899',
  '#84cc16',
];

/**
 * Time Range Selector
 */
function TimeRangeSelector({
  value,
  onChange,
}: {
  value: TimeRange;
  onChange: (range: TimeRange) => void;
}) {
  const options: { value: TimeRange; label: string }[] = [
    { value: '7d', label: '7 Days' },
    { value: '30d', label: '30 Days' },
    { value: '90d', label: '90 Days' },
  ];

  return (
    <div className="flex items-center gap-2">
      {options.map((option) => (
        <Button
          key={option.value}
          variant={value === option.value ? 'default' : 'outline'}
          size="sm"
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </Button>
      ))}
    </div>
  );
}

/**
 * Cost Card Component
 */
function CostCard({
  title,
  value,
  subtitle,
  icon,
  trend,
  isLoading,
}: {
  title: string;
  value: string;
  subtitle?: string;
  icon: string;
  trend?: { value: number; isPositive: boolean };
  isLoading?: boolean;
}) {
  return (
    <Card>
      <CardContent className="pt-6">
        {isLoading ? (
          <>
            <LoadingSkeleton className="h-4 w-24 mb-2" />
            <LoadingSkeleton className="h-8 w-32" />
          </>
        ) : (
          <>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-muted-foreground">{title}</span>
              <span className="text-2xl">{icon}</span>
            </div>
            <div className="text-2xl font-bold">{value}</div>
            {subtitle && (
              <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>
            )}
            {trend && (
              <div
                className={`flex items-center gap-1 mt-2 text-sm ${
                  trend.isPositive ? 'text-red-600' : 'text-green-600'
                }`}
              >
                <span>{trend.isPositive ? '↑' : '↓'}</span>
                <span>{Math.abs(trend.value).toFixed(1)}%</span>
                <span className="text-muted-foreground">vs previous</span>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Agent Cost Table
 */
function AgentCostTable({
  agents,
  isLoading,
}: {
  agents: AgentCostBreakdown[];
  isLoading: boolean;
}) {
  if (isLoading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3, 4, 5].map((i) => (
          <LoadingSkeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    );
  }

  if (agents.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        No cost data available
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="border-b text-left text-sm text-muted-foreground">
            <th className="pb-3 font-medium">Agent</th>
            <th className="pb-3 font-medium">Type</th>
            <th className="pb-3 font-medium text-right">Executions</th>
            <th className="pb-3 font-medium text-right">Total Cost</th>
            <th className="pb-3 font-medium text-right">Avg/Execution</th>
            <th className="pb-3 font-medium text-right">% of Total</th>
          </tr>
        </thead>
        <tbody>
          {agents.map((agent) => (
            <tr key={agent.agentId} className="border-b last:border-0">
              <td className="py-3">
                <Link
                  to={`/agents/${agent.agentId}`}
                  className="font-medium hover:text-primary"
                >
                  {agent.agentName}
                </Link>
              </td>
              <td className="py-3 text-sm text-muted-foreground">
                {agent.agentType}
              </td>
              <td className="py-3 text-sm text-right">
                {formatNumber(agent.executionCount)}
              </td>
              <td className="py-3 text-sm text-right font-medium">
                {formatCost(agent.totalCost)}
              </td>
              <td className="py-3 text-sm text-right">
                {formatCost(agent.averageCostPerExecution)}
              </td>
              <td className="py-3 text-right">
                <div className="flex items-center justify-end gap-2">
                  <div className="w-16 h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-blue-500"
                      style={{ width: `${Math.min(agent.percentage, 100)}%` }}
                    />
                  </div>
                  <span className="text-sm text-muted-foreground w-12">
                    {formatPercentage(agent.percentage)}
                  </span>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Cost Analytics Page Component
 */
export default function CostAnalytics() {
  const [timeRange, setTimeRange] = useState<TimeRange>('30d');
  const { isPro, isLoading: planLoading } = useIsPro();

  const { data, isLoading, error } = useCostAnalytics({ timeRange }, isPro);

  if (!planLoading && !isPro) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-4">
        <div className="max-w-7xl mx-auto py-8">
          <AnalyticsSubNav />
          <ProUpgradeCard
            feature="Cost analytics"
            description="Track spend by agent, trends and projections. Upgrade to Pro ($29/mo) to unlock advanced analytics."
          />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-4">
        <div className="max-w-7xl mx-auto py-8">
          <Card>
            <CardContent className="py-12 text-center">
              <div className="text-6xl mb-4">❌</div>
              <CardTitle className="mb-2">Error Loading Cost Data</CardTitle>
              <CardDescription>
                {error instanceof Error ? error.message : 'An error occurred'}
              </CardDescription>
              <Button
                className="mt-4"
                variant="outline"
                onClick={() => window.location.reload()}
              >
                Retry
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const totalCost = data?.totalCost || 0;
  const trend = data?.trend;
  const byAgent = data?.byAgent || [];
  const byCategory = data?.byCategory || [];
  const projectedCost = data?.projectedMonthlyCost || 0;
  const avgDailyCost = data?.averageDailyCost || 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-4">
      <div className="max-w-7xl mx-auto py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-3xl font-bold">Cost Analytics</h1>
            <p className="text-muted-foreground">
              Track and analyze your spending
            </p>
          </div>
          <div className="flex items-center gap-4">
            <TimeRangeSelector value={timeRange} onChange={setTimeRange} />
            <AnalyticsSubNav />
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <CostCard
            title="Total Cost"
            value={formatCost(totalCost)}
            icon="💰"
            trend={
              trend
                ? {
                    value: trend.changePercentage,
                    isPositive: trend.changePercentage > 0,
                  }
                : undefined
            }
            isLoading={isLoading}
          />
          <CostCard
            title="Daily Average"
            value={formatCost(avgDailyCost)}
            subtitle="Per day"
            icon="📊"
            isLoading={isLoading}
          />
          <CostCard
            title="Projected Monthly"
            value={formatCost(projectedCost)}
            subtitle="Based on current usage"
            icon="📈"
            isLoading={isLoading}
          />
          <CostCard
            title="Top Spender"
            value={byAgent[0]?.agentName || '-'}
            subtitle={byAgent[0] ? formatCost(byAgent[0].totalCost) : undefined}
            icon="🏆"
            isLoading={isLoading}
          />
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Cost Trend Chart */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Cost Trend</CardTitle>
              <CardDescription>Daily spending over time</CardDescription>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <LoadingSkeleton className="h-64 w-full" />
              ) : trend?.data && trend.data.length > 0 ? (
                <ResponsiveContainer width="100%" height={280}>
                  <LineChart data={trend.data}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 12 }}
                      tickFormatter={(value) =>
                        new Date(value).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                        })
                      }
                    />
                    <YAxis
                      tick={{ fontSize: 12 }}
                      tickFormatter={(value) => `$${value.toFixed(2)}`}
                    />
                    <Tooltip
                      labelFormatter={(value) =>
                        new Date(value).toLocaleDateString('en-US', {
                          weekday: 'short',
                          month: 'short',
                          day: 'numeric',
                        })
                      }
                      formatter={(value: number) => [formatCost(value), 'Cost']}
                    />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="cost"
                      name="Daily Cost"
                      stroke="#3b82f6"
                      strokeWidth={2}
                      dot={false}
                      fill="url(#costGradient)"
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-64 flex items-center justify-center text-muted-foreground">
                  No cost data available
                </div>
              )}
            </CardContent>
          </Card>

          {/* Cost by Agent Pie Chart */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Cost by Agent</CardTitle>
              <CardDescription>Spending distribution</CardDescription>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <LoadingSkeleton className="h-64 w-full" />
              ) : byAgent.length > 0 ? (
                <div className="flex items-center">
                  <ResponsiveContainer width="60%" height={280}>
                    <PieChart>
                      <Pie
                        data={byAgent.slice(0, 8)}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={100}
                        paddingAngle={2}
                        dataKey="totalCost"
                        nameKey="agentName"
                      >
                        {byAgent.slice(0, 8).map((_, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={CHART_COLORS[index % CHART_COLORS.length]}
                          />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value: number) => formatCost(value)} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="w-40 space-y-2">
                    {byAgent.slice(0, 6).map((agent, index) => (
                      <div key={agent.agentId} className="flex items-center gap-2">
                        <div
                          className="w-3 h-3 rounded-full flex-shrink-0"
                          style={{
                            backgroundColor: CHART_COLORS[index % CHART_COLORS.length],
                          }}
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium truncate">
                            {agent.agentName}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {formatCost(agent.totalCost)}
                          </p>
                        </div>
                      </div>
                    ))}
                    {byAgent.length > 6 && (
                      <p className="text-xs text-muted-foreground">
                        +{byAgent.length - 6} more
                      </p>
                    )}
                  </div>
                </div>
              ) : (
                <div className="h-64 flex items-center justify-center text-muted-foreground">
                  No data available
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Cost by Category Chart */}
        {byCategory.length > 0 && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="text-lg">Cost by Category</CardTitle>
              <CardDescription>Spending breakdown by cost type</CardDescription>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <LoadingSkeleton className="h-48 w-full" />
              ) : (
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={byCategory} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis
                      type="number"
                      tick={{ fontSize: 12 }}
                      tickFormatter={(value) => `$${value.toFixed(2)}`}
                    />
                    <YAxis
                      type="category"
                      dataKey="category"
                      tick={{ fontSize: 12 }}
                      width={100}
                    />
                    <Tooltip formatter={(value: number) => formatCost(value)} />
                    <Bar
                      dataKey="amount"
                      name="Cost"
                      fill="#3b82f6"
                      radius={[0, 4, 4, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        )}

        {/* Agent Cost Table */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Cost by Agent</CardTitle>
            <CardDescription>Detailed cost breakdown per agent</CardDescription>
          </CardHeader>
          <CardContent>
            <AgentCostTable agents={byAgent} isLoading={isLoading} />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
